/**
 * PerformanceRenderer — Three.js renderer for 3D performance mode.
 *
 * Extends ThreeRenderer, owns its scene, camera, lighting and grid.
 * Lives in the video registry, not the UI layer.
 *
 * Meshes are built via the scene descriptor system — the same code path
 * SceneNode uses. The renderer acts as a virtual SceneNode, automatically
 * wiring all three:group/three:light outputs in the definition as scene inputs.
 * Non-mesh nodes use the visualiser fallback.
 */

import { ThreeRenderer } from '@/plugins/threejs/ThreeRenderer';
import type { Three } from '@/plugins/threejs/types';
import type { GraphIndex } from '@/runtime/GraphIndex';
import type { NodeState, OrbitState } from '@/runtime/RuntimeStore';
import type { SpatialStore } from '@/runtime/SpatialStore';
import type { PerformanceStylesheet } from '@/ui/performance/PerformanceStylesheet';
import { resolveVisualiser } from '@/ui/performance/PerformanceStylesheet';
import { getVisualiser } from '@/ui/performance/VisualiserRegistry';
import type { IRender3D, VisualiserData, MappedValue } from '@/ui/performance/VisualiserRegistry';
import { buildPerNodeDescriptors } from '@/plugins/threejs/buildSceneDescriptor';
import { realizeScene } from '@/plugins/threejs/realizeScene';
import { getAudioEndpoint } from '@/plugins/audio/audioRegistry';
import { getAudioContext } from '@/env/web/AudioContext';
import { logger } from '@/logging';


/** Scale factor: 2D pixels → 3D world units */
export const SCALE = 0.1;

/** Info tracked per visualiser node in the scene */
interface NodeEntry {
  mesh: any;        // Object3D
  nodeId: string;
  render3d: IRender3D | null;
}

/** Definition-level state passed by the UI each frame */
export interface DefinitionSnapshot {
  nodes: Record<string, NodeState>;
  positions: Record<string, { x: number; y: number }>;
  api?: Record<string, import('@/graph/GraphDefinition').ExposedEndpoint>;
}

export class PerformanceRenderer extends ThreeRenderer {
  private instanceId: string;
  private getInstance: (instanceId: string) => any | null;
  private graphIndex: GraphIndex;
  private spatialStore: SpatialStore;

  // All nodes: visualiser + descriptor, tracked uniformly
  private nodeMap = new Map<string, NodeEntry>();

  // Descriptor rebuild tracking
  private lastGraphVersion = -1;
  private descriptorRebuildRequested = false;

  // Selection widget
  private selectedNodeId: string | null = null;
  private selectionWidget: any = null;  // Group with axis lines

  // Orbit state (pan = look-at offset, roll = camera tilt)
  private orbit = { theta: Math.PI / 4, phi: Math.PI / 3, radius: 80, panX: 0, panY: 0, panZ: 0, roll: 0 };

  // Raycaster + helpers (created lazily when three is available)
  private raycaster: any = null;
  private dragPlane: any = null;  // y=0 plane

  // Camera system: perspective (orbit) + orthographic (plan)
  private perspCamera: any = null;
  private orthoCamera: any = null;
  private activeCameraName: string = 'default';

  constructor(
    three: Three,
    instanceId: string,
    getInstance: (instanceId: string) => any | null,
    graphIndex: GraphIndex,
    spatialStore: SpatialStore,
  ) {
    super(three);
    this.instanceId = instanceId;
    this.getInstance = getInstance;
    this.graphIndex = graphIndex;
    this.spatialStore = spatialStore;

    // Create scene, camera, lighting, grid
    this.scene = new three.Scene();
    this.camera = new three.PerspectiveCamera(50, 1, 0.1, 2000);
    this.perspCamera = this.camera;

    // Orthographic camera for plan view (top-down)
    this.orthoCamera = new three.OrthographicCamera(-80, 80, 80, -80, 0.1, 2000);

    // Lighting
    const ambient = new three.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambient);
    const directional = new three.DirectionalLight(0xffffff, 0.8);
    directional.position.set(50, 100, 50);
    this.scene.add(directional);

    // Grid on XZ plane
    const grid = new three.GridHelper(200, 40, 0x334155, 0x1e293b);
    this.scene.add(grid);

    // Raycaster
    this.raycaster = new three.Raycaster();
    this.dragPlane = new three.Plane(new three.Vector3(0, 1, 0), 0);
  }

  // ─── Node mesh lifecycle ───

  /**
   * Sync node meshes with current state. Called per-frame by the UI.
   *
   * All nodes are tracked uniformly in nodeMap:
   * - Mesh/light nodes: built via scene descriptor (same path as SceneNode)
   * - Other nodes: built via visualiser fallback (box/sphere)
   */
  syncNodes(
    definitionState: DefinitionSnapshot,
    instanceState: Record<string, any>,
    stylesheet: PerformanceStylesheet,
    tweakValue: (instanceId: string, nodeId: string, endpointId: string, value: any) => void,
  ): void {
    if (!this.scene) return;

    // ─── Rebuild descriptor nodes on topology/data change ───
    if (this.graphIndex.version !== this.lastGraphVersion || this.descriptorRebuildRequested) {
      this.rebuildDescriptorNodes(definitionState);
      this.lastGraphVersion = this.graphIndex.version;
      this.descriptorRebuildRequested = false;
    }

    const nodeIds = new Set(Object.keys(definitionState.nodes));

    // Remove stale visualiser nodes (descriptor nodes are managed by rebuildDescriptorNodes)
    for (const [id, entry] of this.nodeMap) {
      if (!nodeIds.has(id) && entry.render3d !== null) {
        this.scene.remove(entry.mesh);
        entry.render3d?.dispose();
        this.nodeMap.delete(id);
        this.spatialStore.delete(this.instanceId, id);
      }
    }

    // Add / update non-descriptor nodes
    for (const id of nodeIds) {
      const node = definitionState.nodes[id];
      const position = definitionState.positions[id] ?? { x: 0, y: 0 };

      let entry = this.nodeMap.get(id);

      if (entry) {
        // Per-frame visualiser update (descriptor nodes have render3d=null)
        if (entry.render3d) {
          const values = this.buildMappedValues(node, instanceState, stylesheet);
          entry.render3d.update({ node, instanceId: this.instanceId, position, values, tweakValue });
        }
      } else if (!this.hasMeshOrLightOutput(node.endpoints)) {
        // Non-descriptor node: create via visualiser
        const resolved = resolveVisualiser(node.type, stylesheet, node.endpoints);
        if (!resolved) continue;

        const visualiser = getVisualiser(resolved.visualiserName);
        if (!visualiser?.render3d) continue;

        const values = this.buildMappedValues(node, instanceState, stylesheet);
        const visData: VisualiserData = { node, instanceId: this.instanceId, position, values, tweakValue };

        const render3d = visualiser.render3d(visData);
        const mesh = render3d.createMesh(this.three);
        if (!mesh) continue;

        // Seed from spatial store, fallback to origin
        const sp = this.spatialStore.get(this.instanceId, id);
        mesh.position.set(sp?.x ?? 0, sp?.y ?? 0, sp?.z ?? 0);
        mesh.userData.nodeId = id;

        this.scene.add(mesh);
        this.nodeMap.set(id, { mesh, nodeId: id, render3d });
      }
    }

    // Run animate callbacks (TransformNode.animate() applies posX/posY/posZ to Groups)
    this.runAnimateCallbacks();

    // Write-back: capture current mesh positions into spatial store
    for (const [id, entry] of this.nodeMap) {
      const mp = entry.mesh.position;
      this.spatialStore.set(this.instanceId, id, mp.x, mp.y, mp.z);
    }

    // Push spatial metadata AFTER animate (reads post-transform positions)
    for (const id of nodeIds) {
      const node = definitionState.nodes[id];
      const tracked = this.nodeMap.get(id);
      if (tracked) {
        const mp = tracked.mesh.position;
        this.pushSpatialMetadata(id, node.endpoints, mp.x, mp.y, mp.z);
      }
    }

    // Sync selection widget to follow selected node (e.g. during drag)
    this.syncSelectionWidget();

    // Update camera
    const cam = this.camera!;
    if (this.activeCameraName === 'plan') {
      // Ortho plan view: fixed top-down
      cam.position.set(this.orbit.panX, 500, this.orbit.panZ);
      cam.lookAt(this.orbit.panX, 0, this.orbit.panZ);
    } else {
      // Perspective orbit
      const { theta, phi, radius, panX, panY, panZ, roll } = this.orbit;
      cam.position.set(
        panX + radius * Math.sin(phi) * Math.cos(theta),
        panY + radius * Math.cos(phi),
        panZ + radius * Math.sin(phi) * Math.sin(theta),
      );
      cam.lookAt(panX, panY, panZ);
      if (roll !== 0) {
        cam.rotateZ(roll);
      }
    }

    // Sync audio listener
    this.syncAudioListener();
  }

  // ─── Orbit ───

  getOrbit(): OrbitState {
    return { ...this.orbit };
  }

  setOrbit(orbit: OrbitState): void {
    this.orbit = { ...orbit };
  }

  updateOrbit(dTheta: number, dPhi: number): void {
    if (this.activeCameraName === 'plan') return;
    this.orbit.theta += dTheta;
    this.orbit.phi = Math.max(0.1, Math.min(Math.PI - 0.1, this.orbit.phi + dPhi));
  }

  updatePan(dx: number, dy: number): void {
    if (this.activeCameraName === 'plan') {
      // Plan view: direct XZ pan, scaled by frustum size
      const scale = this.orbit.radius * 0.003;
      this.orbit.panX -= dx * scale;
      this.orbit.panZ -= dy * scale;
      return;
    }
    const { theta } = this.orbit;
    const sensitivity = 0.15;
    // Camera's right vector in XZ plane
    const rightX = -Math.sin(theta);
    const rightZ = Math.cos(theta);
    this.orbit.panX += dx * sensitivity * rightX;
    this.orbit.panY += dy * sensitivity;
    this.orbit.panZ += dx * sensitivity * rightZ;
  }

  updateRoll(dx: number): void {
    if (this.activeCameraName === 'plan') return;
    this.orbit.roll += dx * 0.005;
  }

  updateZoom(delta: number): void {
    this.orbit.radius = Math.max(1, this.orbit.radius + delta);
    if (this.activeCameraName === 'plan') {
      this.updateOrthoFrustum();
    }
  }

  // ─── Camera switching ───

  getActiveCamera(): string {
    return this.activeCameraName;
  }

  setActiveCamera(name: string): void {
    this.activeCameraName = name;
    this.camera = name === 'plan' ? this.orthoCamera : this.perspCamera;
    // Re-apply size for correct aspect ratio
    const canvas = this.getCanvas();
    if (canvas && canvas.width > 0 && canvas.height > 0) {
      this.setSize(canvas.width, canvas.height);
    }
    if (name === 'plan') {
      this.updateOrthoFrustum();
    }
  }

  private updateOrthoFrustum(): void {
    const cam = this.orthoCamera;
    if (!cam) return;
    const canvas = this.getCanvas();
    const aspect = canvas && canvas.height > 0 ? canvas.width / canvas.height : 1;
    const half = this.orbit.radius;
    cam.top = half;
    cam.bottom = -half;
    cam.left = -half * aspect;
    cam.right = half * aspect;
    cam.updateProjectionMatrix();
  }

  // ─── Selection ───

  setSelectedNode(nodeId: string | null): void {
    this.selectedNodeId = nodeId;

    if (!nodeId) {
      // Remove widget
      if (this.selectionWidget && this.scene) {
        this.scene.remove(this.selectionWidget);
      }
      this.selectionWidget = null;
      return;
    }

    // Create widget if needed
    if (!this.selectionWidget) {
      this.selectionWidget = this.createSelectionWidget();
      this.scene!.add(this.selectionWidget);
    }

    // Position on the selected node
    const entry = this.nodeMap.get(nodeId);
    if (entry) {
      this.selectionWidget.position.copy(entry.mesh.position);
    }
  }

  private createSelectionWidget(): any {
    const T = this.three;
    const group = new T.Group();
    const size = 4;
    const colors = [0xff4444, 0x44ff44, 0x4488ff]; // R G B → X Y Z
    const dirs = [
      [size, 0, 0], [0, size, 0], [0, 0, size],
    ];

    for (let i = 0; i < 3; i++) {
      const geo = new T.BufferGeometry().setFromPoints([
        new T.Vector3(0, 0, 0),
        new T.Vector3(dirs[i][0], dirs[i][1], dirs[i][2]),
      ]);
      const mat = new T.LineBasicMaterial({ color: colors[i], linewidth: 2, depthTest: false });
      const line = new T.Line(geo, mat);
      line.renderOrder = 999;
      group.add(line);
    }

    // Ring on XZ plane
    const ringGeo = new T.RingGeometry(3.5, 4, 32);
    const ringMat = new T.MeshBasicMaterial({ color: 0xffffff, side: T.DoubleSide, transparent: true, opacity: 0.3, depthTest: false });
    const ring = new T.Mesh(ringGeo, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.renderOrder = 999;
    group.add(ring);

    return group;
  }

  private syncSelectionWidget(): void {
    if (!this.selectionWidget || !this.selectedNodeId) return;
    const entry = this.nodeMap.get(this.selectedNodeId);
    if (entry) {
      this.selectionWidget.position.copy(entry.mesh.position);
    } else {
      // Selected node removed — clear selection widget
      this.scene?.remove(this.selectionWidget);
      this.selectionWidget = null;
      this.selectedNodeId = null;
    }
  }

  // ─── Raycasting ───

  /**
   * Raycast from NDC coordinates, return the hit nodeId or null.
   */
  raycast(ndcX: number, ndcY: number): string | null {
    if (!this.raycaster || !this.camera) return null;

    this.raycaster.setFromCamera({ x: ndcX, y: ndcY }, this.camera);
    const meshes = Array.from(this.nodeMap.values()).map(e => e.mesh);
    const intersects = this.raycaster.intersectObjects(meshes, true);

    if (intersects.length > 0) {
      let target = intersects[0].object;
      while (target && !target.userData.nodeId) target = target.parent;
      return target?.userData.nodeId ?? null;
    }
    return null;
  }

  /**
   * Raycast from NDC onto the y=0 plane, return world hit point or null.
   */
  raycastToPlane(ndcX: number, ndcY: number): any | null {
    if (!this.raycaster || !this.camera || !this.dragPlane) return null;

    this.raycaster.setFromCamera({ x: ndcX, y: ndcY }, this.camera);
    const hit = new this.three.Vector3();
    const intersected = this.raycaster.ray.intersectPlane(this.dragPlane, hit);
    return intersected ? hit : null;
  }

  /** Get a node's mesh for direct manipulation (drag etc.) */
  getNodeMesh(nodeId: string): any | null {
    return this.nodeMap.get(nodeId)?.mesh ?? null;
  }

  /**
   * If nodeId belongs to a repeater child instance (contains `~`),
   * return all sibling nodeMap keys from the same repeater. Otherwise null.
   */
  getRepeaterGroup(nodeId: string): string[] | null {
    const tildeIdx = nodeId.indexOf('~');
    if (tildeIdx === -1) return null;

    // Extract repeater base: everything before the `~`
    // e.g. "root/repeater_abc~0:transform_xyz" → "root/repeater_abc"
    const repeaterBase = nodeId.substring(0, tildeIdx);
    const siblings: string[] = [];
    for (const key of this.nodeMap.keys()) {
      if (key.startsWith(repeaterBase + '~')) {
        siblings.push(key);
      }
    }
    return siblings.length > 0 ? siblings : null;
  }

  /**
   * Extract the repeater's definition-level nodeId from a child instance key.
   * e.g. "root/repeater_abc~0:transform_xyz" → "repeater_abc"
   */
  getRepeaterNodeId(childKey: string): string | null {
    const tildeIdx = childKey.indexOf('~');
    if (tildeIdx === -1) return null;

    // The repeater base path is everything before `~`
    const repeaterBase = childKey.substring(0, tildeIdx);
    // The last path segment is the repeater's nodeId
    const slashIdx = repeaterBase.lastIndexOf('/');
    return slashIdx !== -1 ? repeaterBase.substring(slashIdx + 1) : repeaterBase;
  }

  // ─── Wheel-over-node value tweaking ───

  /**
   * Check if NDC coords hit a node and return its first tweakable endpoint info.
   */
  getNodeTweakInfo(
    ndcX: number, ndcY: number,
    definitionState: DefinitionSnapshot,
    instanceState: Record<string, any>,
    stylesheet: PerformanceStylesheet,
  ): { nodeId: string; endpointId: string; ep: any; currentValue: any } | null {
    const nodeId = this.raycast(ndcX, ndcY);
    if (!nodeId) return null;

    const node = definitionState.nodes[nodeId];
    if (!node) return null;

    const resolved = resolveVisualiser(node.type, stylesheet, node.endpoints);
    if (!resolved) return null;

    const [, endpointId] = Object.entries(resolved.params)[0] ?? [];
    if (!endpointId) return null;

    const ep = node.endpoints[endpointId];
    if (!ep) return null;

    const nodeValues = instanceState[this.instanceId]?.values?.[node.id] ?? {};
    const payload = nodeValues[endpointId];
    const currentValue = payload && typeof payload === 'object' && 'value' in payload
      ? payload.value
      : payload;

    return { nodeId, endpointId, ep, currentValue };
  }

  // ─── Audio listener ───

  syncAudioListener(): void {
    const cam = this.camera;
    if (!cam) return;

    const audioCtx = getAudioContext() as any;
    if (!audioCtx?.listener) return;

    const l = audioCtx.listener;
    const pos = cam.position;
    const dist = Math.sqrt(pos.x * pos.x + pos.y * pos.y + pos.z * pos.z) || 1;
    const fx = -pos.x / dist, fy = -pos.y / dist, fz = -pos.z / dist;

    const q = cam.quaternion;
    const ux = 2 * (q.x * q.y - q.w * q.z);
    const uy = 1 - 2 * (q.x * q.x + q.z * q.z);
    const uz = 2 * (q.y * q.z + q.w * q.x);

    if (l.positionX) {
      l.positionX.value = pos.x;
      l.positionY.value = pos.y;
      l.positionZ.value = pos.z;
      l.forwardX.value = fx;
      l.forwardY.value = fy;
      l.forwardZ.value = fz;
      l.upX.value = ux;
      l.upY.value = uy;
      l.upZ.value = uz;
    } else if (l.setPosition) {
      l.setPosition(pos.x, pos.y, pos.z);
      l.setOrientation(fx, fy, fz, ux, uy, uz);
    }
  }

  resetAudioListener(): void {
    const ctx = getAudioContext() as any;
    if (!ctx?.listener) return;

    const l = ctx.listener;
    if (l.positionX) {
      l.positionX.value = 0; l.positionY.value = 0; l.positionZ.value = 0;
      l.forwardX.value = 0; l.forwardY.value = 0; l.forwardZ.value = -1;
      l.upX.value = 0; l.upY.value = 1; l.upZ.value = 0;
    } else if (l.setPosition) {
      l.setPosition(0, 0, 0);
      l.setOrientation(0, 0, -1, 0, 1, 0);
    }
  }

  // ─── Dispose ───

  dispose(): void {
    if (this.scene) {
      if (this.selectionWidget) this.scene.remove(this.selectionWidget);
      for (const entry of this.nodeMap.values()) {
        this.scene.remove(entry.mesh);
        entry.render3d?.dispose();
      }
      this.nodeMap.clear();
    }
    this.selectionWidget = null;
    this.selectedNodeId = null;
    this.resetAudioListener();
    super.dispose();
  }

  // ─── Animate ───

  /** Run animate on the root instance — recursively animates all children */
  private runAnimateCallbacks(): void {
    const inst = this.getInstance(this.instanceId);
    if (!inst) {
      logger.trace('[PerformanceRenderer]', `runAnimateCallbacks: getInstance(${this.instanceId}) returned null`);
    }
    inst?.animate();
  }

  // ─── Private helpers ───

  /**
   * Rebuild descriptor nodes: per-node Groups wrapping realized meshes/lights.
   * Each Group goes into nodeMap like any other node — uniform tracking.
   */
  private rebuildDescriptorNodes(definitionState: DefinitionSnapshot): void {
    // Remove old descriptor entries from nodeMap, disposing Three.js resources
    for (const [id, entry] of this.nodeMap) {
      if (entry.render3d === null) {
        // Descriptor nodes have render3d=null
        entry.mesh.traverse((child: any) => {
          if (child.geometry && typeof child.geometry.dispose === 'function') child.geometry.dispose();
          if (child.material && typeof child.material.dispose === 'function') child.material.dispose();
        });
        this.scene!.remove(entry.mesh);
        this.nodeMap.delete(id);
      }
    }

    // Build per-node descriptors
    const perNode = buildPerNodeDescriptors(
      this.graphIndex,
      this.instanceId,
      definitionState.nodes,
      definitionState.api,
    );

    const requestRebuild = () => { this.descriptorRebuildRequested = true; };

    logger.trace(`[PerformanceRenderer] rebuildDescriptorNodes: ${perNode.size} descriptor nodes`);

    // Realize each node's descriptor into a Group
    for (const [nodeId, descriptor] of perNode) {
      logger.trace(`[PerformanceRenderer] realizing ${nodeId}: ${descriptor.meshes.length} meshes, ${descriptor.lights.length} lights, ${descriptor.polylines.length} polylines`);
      for (let mi = 0; mi < descriptor.meshes.length; mi++) {
        const m = descriptor.meshes[mi];
        logger.trace(`[PerformanceRenderer]   mesh[${mi}] transforms=${m.transforms.length} geoRefs=${m.geometryNodeRefs.length} matRef=${m.materialNodeRef.key}`);
      }
      const group = new this.three.Group();

      // Realize into a temporary scene (the group), then move objects
      const result = realizeScene(descriptor, group, this.three, requestRebuild);
      if (result.objects.length === 0) {
        logger.error(`[PerformanceRenderer] realizeScene produced 0 objects for ${nodeId} (descriptor had ${descriptor.meshes.length} meshes)`);
        continue;
      }

      // Position from spatial store, fallback to origin
      const sp = this.spatialStore.get(this.instanceId, nodeId);
      group.position.set(sp?.x ?? 0, sp?.y ?? 0, sp?.z ?? 0);
      group.userData.nodeId = nodeId;

      this.scene!.add(group);
      this.nodeMap.set(nodeId, { mesh: group, nodeId, render3d: null });
    }
  }

  /** Check if a node has three:group or three:light output endpoints */
  private hasMeshOrLightOutput(endpoints: Record<string, any>): boolean {
    for (const ep of Object.values(endpoints)) {
      if (ep.isOutput && (ep.dataType?.split('|').includes('three:group') || ep.dataType === 'any' || ep.dataType?.split('|').includes('three:light'))) {
        return true;
      }
    }
    return false;
  }

  /** Build mapped values for a visualiser update */
  private buildMappedValues(
    node: NodeState,
    instanceState: Record<string, any>,
    stylesheet: PerformanceStylesheet,
  ): Record<string, MappedValue> {
    const resolved = resolveVisualiser(node.type, stylesheet, node.endpoints);
    if (!resolved) return {};

    const nodeValues = instanceState[this.instanceId]?.values?.[node.id] ?? {};
    const values: Record<string, MappedValue> = {};
    for (const [prop, endpointId] of Object.entries(resolved.params)) {
      const ep = node.endpoints[endpointId];
      if (ep) {
        const payload = nodeValues[endpointId];
        const liveValue = payload && typeof payload === 'object' && 'value' in payload
          ? payload.value
          : payload;
        values[prop] = { endpointId, min: ep.min, max: ep.max, value: liveValue };
      }
    }
    return values;
  }

  /** Push spatial metadata onto a node's audio output endpoints */
  private pushSpatialMetadata(nodeId: string, endpoints: Record<string, any>, x: number, y: number, z: number): void {
    if (!this.camera) return;

    // Compute camera-relative stereo pan:
    // project (source - camera) onto camera's right axis
    const cam = this.camera.position;
    const dx = x - cam.x;
    const dy = y - cam.y;
    const dz = z - cam.z;
    const { theta } = this.orbit;
    const rightX = Math.sin(theta);
    const rightZ = -Math.cos(theta);
    const stereoPan = dx * rightX + dz * rightZ;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

    for (const [epId, ep] of Object.entries(endpoints)) {
      if (ep.dataType === 'audio' && ep.isOutput) {
        const audioEp = getAudioEndpoint(this.instanceId, nodeId, epId);
        // x,y,z = world positions (for HRTF mode + AudioListener)
        // stereoPan = camera-relative left/right (for stereo mode)
        // dist = camera-to-source distance (for stereo distance attenuation)
        audioEp?.setValue({ value: { pan: { x, y, z, stereoPan, dist } } });
      }
    }
  }
}
